import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import {
  StarIcon,
  ArrowRightIcon,
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from '@heroicons/react/24/outline';
import PageContainer from '../components/layout/PageContainer';

// Add this interface at the top of the file, after the imports
interface Business {
  id: string;
  name: string;
  image: string;
  stars: number;
  reviews: number;
  description: string;
  address: string;
  category: string;
  phone: string;
  premiumType?: string;
}

// Add this interface after the Business interface
interface Banner {
  id: number;
  url: string;
  image: string;
}

interface Category {
  id: string;
  name: string;
  emoji: string;
  description: string;
  enabled: boolean;
}

// Adicione esta linha após os imports
const AnimatePresenceWrapper = AnimatePresence as React.ComponentType<{
  children: React.ReactNode;
  mode?: "wait" | "sync" | "popLayout";
}>;

// Add shimmer effect component
const ShimmerEffect = () => (
  <div className="animate-shimmer bg-gradient-to-r from-white/5 via-white/10 to-white/5 bg-[length:200%_100%]" />
);

// Add image cache
const imageCache: { [key: string]: HTMLImageElement } = {};
const fallbackCache: { [key: string]: HTMLImageElement } = {};

// Preload image function
const preloadImage = (src: string, category: string): Promise<void> => {
  // Check both caches first
  if (imageCache[src]) {
    return Promise.resolve();
  }
  if (fallbackCache[category]) {
    imageCache[src] = fallbackCache[category];
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      imageCache[src] = img;
      resolve();
    };
    img.onerror = () => {
      // If original image fails, try fallback
      const fallbackSrc = `https://getimage-b7jrylvg3a-uc.a.run.app/categories/${category}.png`;
      
      // Check if fallback is already cached
      if (fallbackCache[category]) {
        imageCache[src] = fallbackCache[category];
        resolve();
        return;
      }

      const fallbackImg = new Image();
      fallbackImg.onload = () => {
        fallbackCache[category] = fallbackImg;
        imageCache[src] = fallbackImg;
        resolve();
      };
      fallbackImg.onerror = () => {
        // If fallback also fails, use a default image
        const defaultImg = new Image();
        defaultImg.onload = () => {
          fallbackCache[category] = defaultImg;
          imageCache[src] = defaultImg;
          resolve();
        };
        defaultImg.onerror = reject;
        defaultImg.src = 'https://getimage-b7jrylvg3a-uc.a.run.app/categories/default.png';
      };
      fallbackImg.src = fallbackSrc;
    };
    img.src = src;
  });
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentCategoryPage, setCurrentCategoryPage] = useState(0);
  const [popularBusinesses, setPopularBusinesses] = useState<Business[]>([]);
  const [bannerImages, setBannerImages] = useState<Banner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [favoritosCategory, setFavoritosCategory] = useState<Category | null>(null);
  const [currentInfoBanner, setCurrentInfoBanner] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [preloadedImages, setPreloadedImages] = useState<{ [key: string]: HTMLImageElement }>({});
  const [loadedBusinessImages, setLoadedBusinessImages] = useState<{ [key: string]: boolean }>({});

  // Load categories from JSON file
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://getbusinesscategorylist-b7jrylvg3a-uc.a.run.app');
        const data = await response.json();
        // Hardcoded Favoritos category
        const favoritos = {
          id: 'favorites',
          name: 'Favoritos',
          emoji: '⭐',
          description: 'Seus lugares favoritos',
          enabled: true
        };
        // Filter out disabled categories
        const otherCategories = data.filter((cat: Category) => cat.enabled);
        setCategories(otherCategories);
        setFavoritosCategory(favoritos);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    fetchCategories();
  }, []);

  // Preload banner images
  useEffect(() => {
    if (bannerImages.length > 0) {
      const newPreloadedImages: { [key: string]: HTMLImageElement } = {};
      
      bannerImages.forEach((banner) => {
        const img = new Image();
        img.src = banner.image;
        newPreloadedImages[banner.image] = img;
      });

      setPreloadedImages(newPreloadedImages);
    }
  }, [bannerImages]);

  // Add auto-slide functionality
  useEffect(() => {
    if (bannerImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [bannerImages.length]);

  // Add auto-slide functionality for categories
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCategoryPage((prev) => {
        const totalPages = Math.ceil(categories.length / 6);
        return (prev + 1) % totalPages;
      });
    }, 8000); // Change page every 8 seconds

    return () => clearInterval(interval);
  }, [categories.length]);

  // Add auto-slide functionality for info banners
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInfoBanner((prev) => (prev + 1) % 4);
    }, 7000); // Change banner every 7 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch('https://getbanners-b7jrylvg3a-uc.a.run.app');
        const data = await response.json();
        setBannerImages(data);
      } catch (error) {
        console.error('Error loading banners:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await fetch('https://getbusinesslist-b7jrylvg3a-uc.a.run.app');
        const data = await response.json();
        // Sort businesses by premium status
        const sortedBusinesses = data
          .sort((a: Business, b: Business) => {
            // First sort by premium status
            const premiumOrder = { gold: 0, silver: 1, bronze: 2, none: 3, '': 3, undefined: 3 };
            const aPremium = a.premiumType || 'none';
            const bPremium = b.premiumType || 'none';
            const premiumDiff = premiumOrder[aPremium as keyof typeof premiumOrder] - premiumOrder[bPremium as keyof typeof premiumOrder];
            
            if (premiumDiff !== 0) return premiumDiff;
            
            // Then sort by rating within each premium tier
            const ratingA = a.reviews > 0 ? a.stars / a.reviews : 0;
            const ratingB = b.reviews > 0 ? b.stars / b.reviews : 0;
            
            // If ratings are equal, sort by number of reviews
            if (ratingA === ratingB) {
              return b.reviews - a.reviews;
            }
            
            return ratingB - ratingA;
          })
          .slice(0, 3); // Get top 3 businesses

        setPopularBusinesses(sortedBusinesses);

        // Preload images for popular businesses
        const preloadPromises = sortedBusinesses.map((business: Business) => 
          preloadImage(business.image, business.category)
        );

        Promise.all(preloadPromises)
          .catch(error => {
            console.error('Error preloading business images:', error);
          });
      } catch (error) {
        console.error('Error loading businesses:', error);
      }
    };

    fetchBusinesses();
  }, []);

  const handleBusinessImageLoad = (imageUrl: string) => {
    setLoadedBusinessImages(prev => ({
      ...prev,
      [imageUrl]: true
    }));
  };

  return (
    <PageContainer>
      {/* Banner Section */}
      <div className="relative h-[200px] mb-6 overflow-hidden rounded-xl">
        {isLoading ? (
          <div className="w-full h-full">
            <ShimmerEffect />
          </div>
        ) : (
          <LazyMotion features={domAnimation}>
            <AnimatePresenceWrapper mode="sync">
              {bannerImages.length > 0 && (
                <motion.img
                  key={currentSlide}
                  src={preloadedImages[bannerImages[currentSlide].image]?.src || bannerImages[currentSlide].image}
                  alt={`Banner ${currentSlide + 1}`}
                  className="w-full h-full object-cover absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </AnimatePresenceWrapper>
          </LazyMotion>
        )}
        {!isLoading && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {bannerImages.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  currentSlide === index ? 'bg-yellow-400' : 'bg-yellow-400/50'
                }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Info Banners Section */}
      <div className="relative h-[40px] mb-6 overflow-hidden rounded-xl">
        <LazyMotion features={domAnimation}>
          <AnimatePresenceWrapper mode="wait">
            <motion.div
              key={currentInfoBanner}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              {currentInfoBanner === 0 && (
                <Link to="/jobs" className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600">
                  <span className="text-white font-semibold">Vagas de Empregos</span>
                </Link>
              )}
              {currentInfoBanner === 1 && (
                <Link to="/news" className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600">
                  <span className="text-white font-semibold">Notícias da Cidade</span>
                </Link>
              )}
              {currentInfoBanner === 2 && (
                <Link to="/coupons" className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-purple-500 to-purple-600">
                  <span className="text-white font-semibold">Cupons de Desconto</span>
                </Link>
              )}
              {currentInfoBanner === 3 && (
                <Link to="/events" className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600">
                  <span className="text-white font-semibold">Eventos Culturais</span>
                </Link>
              )}
            </motion.div>
          </AnimatePresenceWrapper>
        </LazyMotion>
      </div>

      {/* Featured Categories */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">
            Categorias
          </h2>
          <Link
            to="/search"
            className="text-primary text-sm flex items-center gap-1"
          >
            Ver todas
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            {/* Logo always in top-left */}
            <motion.div
              key="logo"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Link
                to="/about"
                className="block aspect-square bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors flex items-center justify-center"
              >
                <img 
                  src="/logo.png" 
                  alt="DizQueTem" 
                  className="h-16 w-16"
                />
              </Link>
            </motion.div>

            {/* Favoritos always second */}
            {favoritosCategory && (
              <motion.div
                key={`favoritos-${currentCategoryPage}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Link
                  to={`/category/${favoritosCategory.id}`}
                  className="block aspect-square bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors flex items-center justify-center"
                >
                  <div className="flex flex-col items-center">
                    <span className="text-4xl mb-1">{favoritosCategory.emoji}</span>
                    <span className="text-white/80 text-xs text-center px-1">
                      {favoritosCategory.name}
                    </span>
                  </div>
                </Link>
              </motion.div>
            )}

            {/* Other categories */}
            {Array.from({ length: 6 }).map((_, index) => {
              const category = categories[currentCategoryPage * 6 + index];
              if (!category) {
                return (
                  <div
                    key={`empty-${currentCategoryPage}-${index}`}
                    className="block aspect-square bg-white/5 backdrop-blur-lg rounded-xl"
                  />
                );
              }
              return (
                <motion.div
                  key={`${category.id}-${currentCategoryPage}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: (index + 2) * 0.1 }}
                >
                  <Link
                    to={`/category/${category.id}`}
                    className="block aspect-square bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors flex items-center justify-center"
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-4xl mb-1">{category.emoji}</span>
                      <span className="text-white/80 text-xs text-center px-1">
                        {category.name}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => {
                const totalPages = Math.ceil(categories.length / 6);
                setCurrentCategoryPage((prev) => (prev - 1 + totalPages) % totalPages);
              }}
              className="w-12 h-6 rounded-lg bg-white/20 backdrop-blur-lg flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <ArrowLongLeftIcon className="h-4 w-4 text-white" />
            </button>
            <div className="flex gap-2">
              {Array.from({ length: Math.ceil(categories.length / 6) }).map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    currentCategoryPage === index ? 'bg-white' : 'bg-white/50'
                  }`}
                  onClick={() => setCurrentCategoryPage(index)}
                />
              ))}
            </div>
            <button
              onClick={() => {
                const totalPages = Math.ceil(categories.length / 6);
                setCurrentCategoryPage((prev) => (prev + 1) % totalPages);
              }}
              className="w-12 h-6 rounded-lg bg-white/20 backdrop-blur-lg flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <ArrowLongRightIcon className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>
      </section>

      {/* Popular Places */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">
            Lugares Populares
          </h2>
          <Link
            to="/businesses"
            className="text-primary text-sm flex items-center gap-1"
          >
            Ver todos
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
        <div className="space-y-3">
          {isLoading ? (
            // Skeleton loading for popular places
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="block bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden"
              >
                <div className="flex gap-3 p-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden">
                    <ShimmerEffect />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="h-4 w-3/4 rounded">
                      <ShimmerEffect />
                    </div>
                    <div className="h-3 w-1/4 rounded">
                      <ShimmerEffect />
                    </div>
                    <div className="h-3 w-2/3 rounded">
                      <ShimmerEffect />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            popularBusinesses.map((business, index) => (
              <motion.div
                key={business.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={`/business/${business.id}`}
                  className={`block backdrop-blur-lg rounded-xl overflow-hidden hover:bg-white/20 transition-colors relative ${
                    business.premiumType === 'gold' || business.premiumType === 'silver'
                      ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50'
                      : 'bg-white/10'
                  }`}
                >
                  {(business.premiumType === 'gold' || business.premiumType === 'silver') && (
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full">
                      Destaque
                    </div>
                  )}
                  <div className="flex gap-3 p-3">
                    <div className="w-20 h-20 rounded-lg overflow-hidden relative">
                      {!loadedBusinessImages[business.image] && (
                        <div className="absolute inset-0">
                          <ShimmerEffect />
                        </div>
                      )}
                      <img 
                        src={imageCache[business.image]?.src || business.image} 
                        alt={business.name}
                        className={`w-full h-full object-cover ${!loadedBusinessImages[business.image] ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                        onLoad={() => handleBusinessImageLoad(business.image)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          const fallbackSrc = `https://getimage-b7jrylvg3a-uc.a.run.app/categories/${business.category}.png`;
                          if (fallbackCache[business.category]) {
                            target.src = fallbackCache[business.category].src;
                          } else {
                            target.src = fallbackSrc;
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium mb-1 truncate">
                        {business.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-1">
                        <StarIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white/80 text-sm">{business.stars === 0 ? '0' : (business.stars/business.reviews).toFixed(1)}</span>
                        <span className="text-white/60 text-sm">({business.reviews})</span>
                      </div>
                      <p className="text-white/60 text-sm truncate">
                        {business.description}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white/10 backdrop-blur-lg rounded-xl p-4">
        <h2 className="text-xl font-semibold text-white mb-4">
          Como Funciona
        </h2>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-primary text-sm font-medium">1</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">
                Busque
              </h3>
              <p className="text-white/60 text-sm">
                Encontre estabelecimentos por categoria ou use a busca.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-primary text-sm font-medium">2</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">
                Avalie
              </h3>
              <p className="text-white/60 text-sm">
                Leia avaliações reais e compartilhe suas experiências.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-primary text-sm font-medium">3</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">
                Descubra
              </h3>
              <p className="text-white/60 text-sm">
                Encontre os melhores lugares próximos a você.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default Home; 